import { useMemo, useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { DEFAULT_PROFITABILITY_COLUMNS, Tables, hashCode } from "@repo/utils";
import { generateWorkTypeId } from "@repo/structure";
import { useInView } from "react-intersection-observer";
import { useStores } from "@hooks";
import {
  useEstimateStructure,
  useEstimateEditorStore,
  usePresetFieldNames,
  useSummaryColumnWidthClasses,
  useUserVisibleWorkTypes,
  useVisibilityModeCellClassName,
  useTableExpandHandler,
  useEstimateEditorSettings,
  EstimateTableSettingsProvider,
  TableTitle,
  PinnableCell,
  Totals,
  TableContainer,
  BottomBar,
  PriceModifierCreator,
} from "@tools";
import { AddButtonFull, Checkbox } from "@components"
import { Grid, Collapse } from "@material-ui/core";
import { SummaryRowGroupList } from "./components/SummaryRowGroupList";
import { FixedPriceRow } from "./components/BreakdownPriceRows/FixedPriceRow";
import { MaterialsRow } from "./components/BreakdownPriceRows/MaterialsRow";
import { TableHeader } from "./components/TableHeader/TableHeader";
import { CurrencyExchangeSettings } from "./components/CurrencyExchangeSettings";
import { SummaryTableActions } from "./components/SummaryTableActions";
import { PriceModifierList } from "./components/PriceModifierList";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";

export const TableSummary = observer(({
  tableBreakdownNext,
  tableBreakdownPrev,
  noBottomBar,
  isLastTable,
}) => {
  const {
    allowEdition,
    isProposal,
    readOnly,
    blockExpansions,
    forceExpansionArrows,
    useProfitability,
    showPdfCover,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { stateStore } = useStores();
  const structure = useEstimateStructure();
  const { visibilityMode, hideBottomBar, isAiGenerating } = useEstimateEditorStore();
  const { t } = useTranslation();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const classes = useStyle();
  const colClasses = useSummaryColumnWidthClasses()();
  
  const [ headerRef, setHeaderRef ] = useState(null);
  
  const [ inViewRef, summaryVisible ] = useInView({ root: stateStore.scrollRef, initialInView: true });
  
  const { workTypes, hasFixedPriceBreakdownElements, hasMaterialBreakdownElements, workTypesHaveResources, visiblePriceModifiers, allWorkTypesAreHidden } = structure;
  const { showSummary, currencyObj, visibleSummaryColumns, companyCurrency, currency, useMinMax, maxValuesAsBuffer } = structure.settings;
  const { apply, hideSummary, hideMinValues, hideMaxValues, useAvgValues, hideSummaryFixedCost, hideSummaryMaterials } = structure.visibility;
  
  const className = useVisibilityModeCellClassName(hideSummary);
  
  const visibleWorkTypes = useUserVisibleWorkTypes(workTypes, isProposal);

  const useExpandCell = useMemo(() => (
    /*hasTeamMembersCol &&*/ !(isProposal || readOnly) || workTypesHaveResources || forceExpansionArrows
  ), [isProposal, readOnly, workTypesHaveResources, forceExpansionArrows]);
  
  const columns = useMemo(() => (
    useProfitability
      ? DEFAULT_PROFITABILITY_COLUMNS
      : visibleSummaryColumns
  ), [useProfitability, visibleSummaryColumns]);
  
  const tableContainerIdentifier = "summaryContainer";
  const tableIdentifier = "summaryHeader";

  const addNewWorkType = () => {
    structure.historyManager.startGroup();
    structure.addWorkType(true, { id: generateWorkTypeId() });
  };
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: !blockExpansions,
    isOpened: showSummary,
    onExpand: structure.settings.setShowSummary
  });

  const handleWorkTypeReorder = (list) => structure.reorderWorkTypes(list);
  
  const handleUseAvgValuesChange = () => {
    structure.historyManager.startGroup();
    structure.visibility.setUseAvgValues(!useAvgValues)
    structure.generateTimeline();
    structure.historyManager.stopGroup();
  };
  
  return (
    <EstimateTableSettingsProvider
      useExpandCell={useExpandCell}
      columns={columns}
    >
      <Grid
        item container
        ref={inViewRef}
        id={Tables.SUMMARY}
        justifyContent="center"
        alignItems="center"
        className={classnames(
          "preset-paper",
          classes.whiteContainer,
          (tableBreakdownNext || useProfitability) && classes.whiteContainerLast,
          tableBreakdownPrev && classes.whiteContainerFirst,
          isLastTable && !isPdfPrinterView && classes.paddingBottom,
        )}
      >
        <Grid
          item container
          justifyContent="flex-end"
          className={classnames(
            "vCon",
            showPdfCover && "pdf-mode",
            isPdfPrinterView && "printer-mode",
            classes.root,
            colClasses.root,
          )}
        >
          {
            (!apply || !hideSummary) && !allWorkTypesAreHidden &&
            <>
              <TableTitle
                disabled={visibilityMode && hideSummary}
                expandable={!blockExpansions && !useProfitability}
                presetClass="summary"
                buttonPresetClass="preset-tableSummaryExpandButton"
                id={hashCode(Tables.SUMMARY)}
                title={getFieldName("pt-summary")}
                showContent={showSummary}
                onContentAction={handleOpen}
                className={classnames(isPdfPrinterView && "printer-mode")}
                action={!isPdfPrinterView && <SummaryTableActions />}
              >
                {
                  visibilityMode && (useMinMax || maxValuesAsBuffer) && !useProfitability &&
                  <Grid
                    item container
                    className="w-max"
                    alignItems="center"
                  >
                    <Checkbox
                      name="min"
                      checked={!hideMinValues}
                      disabled={useAvgValues}
                      label={t("common.min")}
                      onChange={() => structure.visibility.setHideMinValues(!hideMinValues)}
                    />
                    <Checkbox
                      name="avg"
                      checked={Boolean(useAvgValues)}
                      label={t("common.avg")}
                      onChange={handleUseAvgValuesChange}
                      className="ml-2"
                    />
                    <Checkbox
                      name="max"
                      checked={!hideMaxValues}
                      disabled={useAvgValues}
                      label={t("common.max")}
                      onChange={() => structure.visibility.setHideMaxValues(!hideMaxValues)}
                      className="ml-2"
                    />
                    {/*<MinMaxSwitch />*/}
                  </Grid>
                }
                {
                  allowEdition && useProfitability && companyCurrency !== currency &&
                  <CurrencyExchangeSettings />
                }
              </TableTitle>
              <Collapse
                in={showSummary || useProfitability}
                timeout="auto"
                mountOnEnter
                unmountOnExit
                className={classnames(showPdfCover && "pdf-mode", isPdfPrinterView && "printer-mode", classes.collapse, className)}
                data-id={hashCode("cont")}
                data-t={Tables.SUMMARY}
                data-p={hashCode(Tables.SUMMARY)}
                { ...collapseProps }
              >
                <TableContainer
                  tableHeaderRef={headerRef}
                  tableContainerIdentifier={tableContainerIdentifier}
                  className={classnames("sectionContainer", isPdfPrinterView ? "w-full mb-4" : classes.tableContainer)}
                  useExpandCell={useExpandCell}
                >
                  <TableHeader
                    ref={setHeaderRef}
                    tableIdentifier={tableIdentifier}
                    currencyCode={currencyObj.code}
                  />
                  <SummaryRowGroupList
                    listElements={visibleWorkTypes}
                    onReorder={handleWorkTypeReorder}
                    isLastGroupRow
                    blockExpansions={blockExpansions}
                    disabled={!allowEdition}
                    hashedPath={[hashCode(Tables.SUMMARY)]}
                  />
                  {
                    hasFixedPriceBreakdownElements && (!apply || !hideSummaryFixedCost) &&
                    <FixedPriceRow />
                  }
                  {
                    hasMaterialBreakdownElements && (!apply || !hideSummaryMaterials) &&
                    <MaterialsRow />
                  }
                  {
                    visiblePriceModifiers?.length > 0 &&
                    <PriceModifierList />
                  }
                  {
                    allowEdition && !useProfitability && workTypes.length === visibleWorkTypes.length &&
                    <Grid item container role="row" wrap="nowrap">
                      <PinnableCell
                        tableDepth={-1}
                        pinnedLeft
                        stretchToChildrenWidth
                      >
                        <AddButtonFull
                          id="newWorkTypeButton"
                          onClick={addNewWorkType}
                          name="Add new work type to summary"
                        >
                          {t("views.editor.summary_table.work_add")}
                        </AddButtonFull>
                        <PriceModifierCreator />
                      </PinnableCell>
                    </Grid>
                  }
                </TableContainer>
              </Collapse>
            </>
          }
          <Totals useBottomBar={useProfitability && !isAiGenerating && !noBottomBar && (!isProposal || !hideBottomBar)} />
          {
            !useProfitability && !isAiGenerating && !noBottomBar && (!isProposal || !hideBottomBar) &&
            <BottomBar summaryHidden={!summaryVisible} />
          }
        </Grid>
      </Grid>
    </EstimateTableSettingsProvider>
  );
});

TableSummary.propTypes = {
  tableBreakdownNext: bool,
  tableBreakdownPrev: bool,
  noBottomBar: bool,
  isLastTable: bool,
};
